import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { FormInstance } from 'antd';
import { Button, Col, DatePicker, Form, Image, Input, Modal, Row, Select, Upload, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ChevronDown, Upload as UploadIcon, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usersService } from '@/services/users.service';
import { buildFullUrl } from '@/contexts/AvatarContext';
import styles from '../../pages/UpdatePersonalProfilePage.module.css';

const SKILL_SUGGESTIONS: string[] = [
  'Giao tiếp',
  'Làm việc nhóm',
  'Quản lý thời gian',
  'Thuyết trình',
  'Excel',
  'Word',
  'PowerPoint',
];

interface UpdateCertificatesSkillsSectionProps {
  /**
   * userId của nhân sự cần hiển thị chứng chỉ và kỹ năng.
   * - Nếu truyền vào: ưu tiên dùng userId này (dùng cho màn Quản lý nhân sự chỉnh hồ sơ staff).
   * - Nếu không truyền: fallback về user hiện tại từ AuthContext (dùng cho trang hồ sơ cá nhân tự cập nhật).
   */
  userId?: number;
  form: FormInstance;
  onAttachmentUploaded?: (pending: boolean) => void;
}

export const UpdateCertificatesSkillsSection: React.FC<UpdateCertificatesSkillsSectionProps> = ({ userId, form, onAttachmentUploaded }) => {
  const { user } = useAuth();
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [removingUrl, setRemovingUrl] = useState<string | null>(null);
  const loadedPendingFor = useRef<string | null>(null);
  const formCertificates = Form.useWatch('certificates', form) as Array<{ id?: number; attachments?: Array<{ id?: number; fileName: string; fileUrl: string; pending?: boolean; pendingRemoval?: boolean }> }> | undefined;
  const certificateKey = (formCertificates || []).map((certificate) => certificate.id).filter(Boolean).join(',');

  const getTargetUserId = useCallback((): number | null => {
    if (typeof userId === 'number') return userId;
    if (user?.id) return parseInt(user.id.toString(), 10);
    return null;
  }, [userId, user?.id]);

  // Ảnh chờ duyệt không nằm trong bảng chứng chỉ cho tới khi được duyệt.
  useEffect(() => {
    const targetUserId = getTargetUserId();
    if (!targetUserId || !certificateKey) return;
    const loadKey = `${targetUserId}:${certificateKey}`;
    if (loadedPendingFor.current === loadKey) return;
    loadedPendingFor.current = loadKey;
    let active = true;
    usersService.getUpdateRequests({ userId: targetUserId, status: 'pending', limit: 1 }).then((result) => {
      if (!active) return;
      const updateData = result.data?.[0]?.updateData || {};
      const pendingAttachments = Array.isArray(updateData.certificateAttachments) ? updateData.certificateAttachments : [];
      const deleteIds = new Set<number>(Array.isArray(updateData.certificateAttachmentDeleteIds) ? updateData.certificateAttachmentDeleteIds : []);
      const certificates = form.getFieldValue('certificates') || [];
      certificates.forEach((certificate: any, index: number) => {
        const existing = certificate.attachments || [];
        const additions = pendingAttachments.filter((attachment: any) => attachment.certificateId === certificate.id && !existing.some((item: any) => item.fileUrl === attachment.fileUrl));
        const next = [...existing.map((item: any) => ({ ...item, pendingRemoval: deleteIds.has(item.id) })), ...additions.map((item: any) => ({ ...item, pending: true }))];
        if (additions.length > 0 || existing.some((item: any) => !!item.pendingRemoval !== deleteIds.has(item.id))) {
          form.setFieldValue(['certificates', index, 'attachments'], next);
        }
      });
    }).catch(() => { loadedPendingFor.current = null; });
    return () => { active = false; };
  }, [form, certificateKey, getTargetUserId]);

  const uploadCertificateAttachment = useCallback(
    async (certificateIndex: number, file: File): Promise<boolean> => {
      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
        message.error('Chỉ nhận ảnh JPG, PNG, GIF, WebP tối đa 5MB');
        return false;
      }
      const targetUserId = getTargetUserId();
      if (!targetUserId || Number.isNaN(targetUserId)) {
        message.error('Không tìm thấy người dùng để upload tệp đính kèm');
        return false;
      }

      const certificates = form.getFieldValue('certificates') || [];
      const cert = certificates[certificateIndex];
      const certId = cert?.id;

      if (!certId) {
        message.warning(user?.role?.toLowerCase() === 'staff'
          ? 'Chứng chỉ mới cần được phê duyệt trước khi tải ảnh. Hãy gửi yêu cầu cập nhật rồi mở lại trang sau khi được duyệt.'
          : 'Vui lòng lưu chứng chỉ trước khi tải ảnh đính kèm.');
        return false;
      }

      setUploadingIndex(certificateIndex);
      try {
        const response = await usersService.uploadCertificateAttachment(targetUserId, certId, file);
        if (!response.success || !response.data?.fileUrl) {
          throw new Error(response.message || 'Server chưa xác nhận ảnh chứng chỉ');
        }
        const currentCert = (form.getFieldValue('certificates') || [])[certificateIndex];
        form.setFieldValue(['certificates', certificateIndex, 'attachments'], [
          ...(currentCert?.attachments || []),
          { ...response.data, pending: !!response.requestId },
        ]);
        onAttachmentUploaded?.(!!response.requestId);
        message.success(response.requestId
          ? `Đã gửi ảnh chứng chỉ trong yêu cầu #${response.requestId}, chờ phê duyệt`
          : 'Đã lưu ảnh chứng chỉ. Không cần bấm Cập nhật nếu bạn chỉ thay ảnh.');
      } catch (error: any) {
        message.error(error?.message || 'Không thể upload tệp đính kèm chứng chỉ');
      } finally {
        setUploadingIndex(null);
      }

      return false;
    },
    [form, getTargetUserId, onAttachmentUploaded, user?.role],
  );

  const removeAttachment = useCallback(async (certificateIndex: number, attachment: { id?: number; fileUrl: string; pending?: boolean }) => {
    const targetUserId = getTargetUserId();
    const certificate = (form.getFieldValue('certificates') || [])[certificateIndex];
    if (!targetUserId || !certificate?.id) return;
    setRemovingUrl(attachment.fileUrl);
    try {
      const response = attachment.pending
        ? await usersService.removePendingCertificateAttachment(targetUserId, certificate.id, attachment.fileUrl)
        : await usersService.removeCertificateAttachment(targetUserId, certificate.id, attachment.id!);
      const attachments = certificate.attachments || [];
      form.setFieldValue(['certificates', certificateIndex, 'attachments'], response.requestId
        ? attachments.map((item: any) => item.id === attachment.id ? { ...item, pendingRemoval: true } : item)
        : attachments.filter((item: any) => item.fileUrl !== attachment.fileUrl));
      onAttachmentUploaded?.(!!response.requestId);
      message.success(response.message);
    } catch (error: any) {
      message.error(error?.message || 'Không thể xóa ảnh chứng chỉ');
    } finally {
      setRemovingUrl(null);
    }
  }, [form, getTargetUserId, onAttachmentUploaded]);

  const confirmRemoveAttachment = (certificateIndex: number, attachment: { id?: number; fileUrl: string; pending?: boolean }) => {
    Modal.confirm({
      title: 'Xóa ảnh chứng chỉ?',
      content: attachment.pending
        ? 'Ảnh sẽ được rút khỏi yêu cầu chờ duyệt.'
        : user?.role?.toLowerCase() === 'staff'
          ? 'Yêu cầu xóa ảnh sẽ được gửi để phê duyệt.'
          : 'Ảnh sẽ được xóa ngay khỏi chứng chỉ.',
      okText: 'Xóa ảnh',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: () => removeAttachment(certificateIndex, attachment),
    });
  };

  return (
    <div className={styles.section}>
      <Form.List name="certificates">
        {(fields, { add, remove }) => (
          <div className={styles.familySection}>
            {fields.length === 0 && (
              <div className={styles.familyEmptyState}>
                <div style={{ marginBottom: 16 }}>
                  Chưa có chứng chỉ nào. Nhấn &quot;Thêm chứng chỉ&quot; để bắt đầu.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<Plus size={16} />}
                    size="middle"
                    className={styles.familyAddButton}
                    onClick={() => add({})}
                  >
                    Thêm chứng chỉ
                  </Button>
                </div>
              </div>
            )}

            {fields.map((field, index) => (
              <div key={field.key} className={styles.workCard}>
                <div className={styles.workCardHeader}>
                  <span className={styles.workCardSubtitle}>Chứng chỉ {index + 1}</span>
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    className={styles.workCardRemoveButton}
                    onClick={() => remove(field.name)}
                  >
                    Xóa
                  </Button>
                </div>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={8}>
                    <Form.Item name={[field.name, 'certificateNumber']} label="Nhập số hiệu">
                      <Input placeholder="Số hiệu chứng chỉ" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={[field.name, 'issuingBody']} label="Đơn vị ban hành">
                      <Input placeholder="Đơn vị ban hành" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={[field.name, 'certificateType']} label="Loại chứng chỉ">
                      <Input placeholder="Nhập loại chứng chỉ" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'fromDate']} label="Từ ngày">
                      <DatePicker
                        placeholder="Chọn ngày"
                        size="large"
                        format="DD/MM/YYYY"
                        className={styles.fullWidth}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'toDate']} label="Đến ngày">
                      <DatePicker
                        placeholder="Chọn ngày"
                        size="large"
                        format="DD/MM/YYYY"
                        className={styles.fullWidth}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={24}>
                    <Form.Item name={[field.name, 'notes']} label="Phần ghi chú">
                      <TextArea
                        placeholder="Ghi chú thêm"
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        className={styles.textareaNoResize}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <div className={styles.certificateUploadPanel}>
                  <div className={styles.certificateUploadLabel}>Ảnh đính kèm chứng chỉ</div>
                  <div className={styles.certificateUploadAction}>
                    <Upload
                      multiple={false}
                      showUploadList={false}
                      beforeUpload={(file) => uploadCertificateAttachment(index, file as File)}
                      disabled={uploadingIndex === index}
                    >
                      <Button icon={<UploadIcon size={16} />} loading={uploadingIndex === index}>Chọn ảnh</Button>
                    </Upload>
                    <span>JPG, PNG, GIF, WebP · tối đa 5MB</span>
                  </div>
                  <div className={styles.certificateAttachmentList}>
                    {(formCertificates?.[field.name]?.attachments || []).map((attachment) => (
                      <div key={attachment.id ?? attachment.fileUrl} className={styles.certificateAttachmentItem}>
                        <Image src={buildFullUrl(attachment.fileUrl) || undefined} alt={attachment.fileName || 'Ảnh chứng chỉ'} width={72} height={72} style={{ objectFit: 'cover' }} />
                        <div className={styles.certificateAttachmentInfo}>
                          <div className={styles.certificateAttachmentName} title={attachment.fileName}>{attachment.fileName}</div>
                          {(attachment.pending || attachment.pendingRemoval) && <div className={styles.certificateAttachmentStatus}>{attachment.pendingRemoval ? 'Chờ phê duyệt xóa' : 'Chờ phê duyệt thêm'}</div>}
                        </div>
                        {!attachment.pendingRemoval && <Button type="text" danger icon={<Trash2 size={16} />} aria-label={`Xóa ảnh ${attachment.fileName}`} loading={removingUrl === attachment.fileUrl} onClick={() => confirmRemoveAttachment(index, attachment)} />}
                      </div>
                    ))}
                    {!(formCertificates?.[field.name]?.attachments || []).length && <span className={styles.certificateAttachmentEmpty}>Chưa có ảnh đính kèm.</span>}
                  </div>
                </div>
              </div>
            ))}

            {/* Nút Thêm chứng chỉ ở cuối danh sách */}
            {fields.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<Plus size={16} />}
                  size="middle"
                  className={styles.familyAddButton}
                  onClick={() => add({})}
                >
                  Thêm chứng chỉ
                </Button>
              </div>
            )}
          </div>
        )}
      </Form.List>

      <div className={styles.skillsSection}>
        <Row gutter={24} className={styles.formRow}>
          <Col span={24}>
            <Form.Item name="skills">
              <Select
                mode="tags"
                size="large"
                placeholder="Nhập kỹ năng"
                className={styles.select}
                suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                tokenSeparators={[',']}
                options={SKILL_SUGGESTIONS.map((s) => ({ label: s, value: s }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </div>
  );
};
